import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { useGetQualityInspectionProcurement } from '@/hooks/procurement/use-get-quality-inspection-procurement';
import { useQualityInspectionActions } from '@/components/quality/hooks/useQualityInspectionActions';
import QualityInspectionForm, { QualityInspectionData } from '@/components/quality/QualityInspectionForm';
import QualityProcurementDetailsCard from '@/components/quality/QualityProcurementDetailsCard';
import ItemQualityInspectionForm from '@/components/quality/ItemQualityInspectionForm';
import { ItemQualityData } from '@/types/database/procurement-item-quality';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CheckCircle, XCircle, Clock, ClipboardCheck } from 'lucide-react';

const QUALITY_REJECTION_REASONS = {
  high_damage: 'High damage percentage',
  sample_failed: 'Sample quality test failed',
  contamination: 'Product contamination detected',
  size_mismatch: 'Sample size does not match specifications',
  quality_below_standard: 'Quality below acceptable standard',
  other: 'Other'
};

const QualityInspection = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { data: procurement, isLoading } = useGetQualityInspectionProcurement(id || '');
  
  const [inspectionData, setInspectionData] = useState<QualityInspectionData>({
    sampleSizeChecked: false,
    sampleSizeNotes: '',
    damageKg: 0,
    compensationKg: 0,
    damagePercentage: 0,
    damageNotes: '',
    qualityGrade: '',
    rejectionReason: '',
    gradeBreakdown: [],
    gradingType: 'final',
    finalGradingExpectedDate: '',
    preliminaryNotes: ''
  });

  // Item-level quality data for multi-item procurements
  const [itemQualityData, setItemQualityData] = useState<ItemQualityData[]>([]);

  const isMultiItem = (procurement?.items?.length || 0) > 1;

  // Initialize item quality data when procurement loads
  useEffect(() => {
    if (procurement?.items && procurement.items.length > 1) {
      setItemQualityData(procurement.items.map(item => ({
        itemId: item.id,
        productName: item.product_name,
        quantity: item.quantity,
        qualityGrade: '',
        damageKg: 0,
        compensationKg: 0,
        damagePercentage: 0,
        notes: '',
        isRejected: false
      })));
    }
  }, [procurement?.items]);

  // Sync item totals to main inspection data for multi-item
  useEffect(() => {
    if (isMultiItem && itemQualityData.length > 0) {
      const totals = itemQualityData.reduce((acc, item) => ({
        damageKg: acc.damageKg + (item.damageKg || 0),
        compensationKg: acc.compensationKg + (item.compensationKg || 0)
      }), { damageKg: 0, compensationKg: 0 });

      setInspectionData(prev => ({
        ...prev,
        damageKg: totals.damageKg,
        compensationKg: totals.compensationKg
      }));
    }
  }, [itemQualityData, isMultiItem]);

  const { handleApprove, handleReject, isSubmitting } = useQualityInspectionActions(procurement, itemQualityData);

  const handleBack = () => {
    navigate('/quality');
  };

  const onApprove = () => handleApprove(inspectionData);
  const onReject = () => handleReject(inspectionData);

  const isPreliminary = inspectionData.gradingType === 'preliminary';

  if (isLoading) {
    return (
      <Layout fullWidth>
        <div className="p-6 space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!procurement) {
    return (
      <Layout fullWidth>
        <div className="p-6 space-y-6">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quality
          </Button>
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">Procurement not found</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout fullWidth>
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quality
          </Button>
          <h1 className="text-2xl font-bold">Quality Inspection</h1>
          <span className="text-muted-foreground">#{procurement.id.slice(0, 8)}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <QualityProcurementDetailsCard procurement={procurement} />
          
          {isMultiItem ? (
            // Multi-item inspection form
            <Card>
              <CardHeader>
                <CardTitle>Quality Inspection Form</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Grading Type Selection */}
                <div className="space-y-3 p-4 bg-muted/50 rounded-lg border">
                  <Label className="text-sm font-medium">Grading Type</Label>
                  <RadioGroup
                    value={inspectionData.gradingType}
                    onValueChange={(value: 'preliminary' | 'final') =>
                      setInspectionData(prev => ({ ...prev, gradingType: value }))
                    }
                    className="grid grid-cols-1 gap-3"
                  >
                    <div className="flex items-start space-x-3 p-3 rounded-md border bg-background">
                      <RadioGroupItem value="preliminary" id="preliminary" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="preliminary" className="flex items-center gap-2 font-medium cursor-pointer">
                          <Clock className="h-4 w-4 text-amber-500" />
                          Preliminary (Sample-based)
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Quick assessment based on sample. Payment proceeds now, full grading later.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 p-3 rounded-md border bg-background">
                      <RadioGroupItem value="final" id="final" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="final" className="flex items-center gap-2 font-medium cursor-pointer">
                          <ClipboardCheck className="h-4 w-4 text-green-500" />
                          Final (Complete Grading)
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Full quality assessment completed. This is the final determination.
                        </p>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* Expected Final Grading Date - Only show for preliminary */}
                {isPreliminary && (
                  <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <Label htmlFor="finalGradingExpectedDate" className="text-amber-800">
                      Expected Final Grading Date
                    </Label>
                    <Input
                      id="finalGradingExpectedDate"
                      type="date"
                      value={inspectionData.finalGradingExpectedDate}
                      onChange={(e) =>
                        setInspectionData(prev => ({ ...prev, finalGradingExpectedDate: e.target.value }))
                      }
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sampleSizeChecked"
                    checked={inspectionData.sampleSizeChecked}
                    onCheckedChange={(checked) =>
                      setInspectionData(prev => ({ ...prev, sampleSizeChecked: !!checked }))
                    }
                  />
                  <Label htmlFor="sampleSizeChecked">Sample size verified and acceptable</Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sampleSizeNotes">Sample Size Notes</Label>
                  <Textarea
                    id="sampleSizeNotes"
                    value={inspectionData.sampleSizeNotes}
                    onChange={(e) =>
                      setInspectionData(prev => ({ ...prev, sampleSizeNotes: e.target.value }))
                    }
                    placeholder="Add notes about sample size verification..."
                    rows={2}
                  />
                </div>

                {/* Per-Item Quality Inspection */}
                <div className="pt-4 border-t">
                  <ItemQualityInspectionForm
                    items={procurement.items || []}
                    itemQualityData={itemQualityData}
                    setItemQualityData={setItemQualityData}
                    gradingType={inspectionData.gradingType}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rejectionReason">Rejection Reason (if rejecting)</Label>
                  <Select
                    value={inspectionData.rejectionReason}
                    onValueChange={(value) =>
                      setInspectionData(prev => ({ ...prev, rejectionReason: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select rejection reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(QUALITY_REJECTION_REASONS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Info box for preliminary grading */}
                {isPreliminary && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
                    <p className="font-medium mb-1">ℹ️ Preliminary Assessment</p>
                    <p>Payment will proceed based on this sample assessment. Finance will be notified to compare with final grading when completed.</p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={onApprove}
                    disabled={isSubmitting || !inspectionData.sampleSizeChecked || (isPreliminary && !inspectionData.finalGradingExpectedDate)}
                    className="flex-1"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {isPreliminary ? 'Approve (Preliminary)' : 'Approve Quality'}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={onReject}
                    disabled={isSubmitting || !inspectionData.rejectionReason}
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            // Single-item uses the original form
            <QualityInspectionForm
              inspectionData={inspectionData}
              setInspectionData={setInspectionData}
              onApprove={onApprove}
              onReject={onReject}
              isSubmitting={isSubmitting}
              expectedQuantity={procurement.total_quantity || undefined}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default QualityInspection;
